import { useId, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useI18n } from '../i18n';
import { LanguageMenu } from '../components/LanguageMenu';
import { ThemeMenu } from '../components/ThemeMenu';
import { PasswordInput } from '../components/PasswordInput';
import { useAuth } from './AuthProvider';
import { AuthApiError } from './auth-api';
import { redirectTargetFrom } from './redirect-target';

/**
 * Écran de connexion (T7a + multi-login T7c) : carte centrée avec sélecteur de
 * langue en tête, champ « Identifiant ou email », mot de passe révélable et case
 * « Rester connecté » (cochée par défaut). Sert le premier login ET l'ajout d'un
 * compte (empilage).
 *
 * Message d'erreur générique (anti-énumération, aligné sur le 401 back). Après
 * succès, retour à la page visée (`state.from`) ou à l'accueil. Le sélecteur de
 * thème reste accessible dans le coin de la page. Tokens sémantiques.
 */
export function LoginScreen(): React.JSX.Element {
  const { t } = useI18n();
  const { login, accounts } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Page visée AVANT le gate de login, reconstituée ENTIÈREMENT (`pathname + search +
  // hash`) : n'en garder que le `pathname` perdait la query d'un lien partagé
  // (`/dossiers?dossier=…`, `/?q=…`) → cf. `redirect-target.ts`.
  const from = redirectTargetFrom(location.state);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [stayLoggedIn, setStayLoggedIn] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const errorId = useId();

  // `finally` et NON un `setSubmitting(false)` dans le seul `catch` (audit F9) : si la
  // navigation post-login n'a pas lieu (route absente, redirection interceptée), le
  // bouton restait verrouillé À VIE, sans message ni recours.
  const submit = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password, stayLoggedIn);
      navigate(from, { replace: true });
    } catch (err: unknown) {
      if (err instanceof AuthApiError && err.status === 401) setError(t('auth.invalidCredentials'));
      else if (err instanceof AuthApiError && err.status === 409)
        setError(t('auth.tooManyAccounts'));
      else setError(t('auth.genericError'));
    } finally {
      setSubmitting(false);
    }
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (submitting) return;
    void submit();
  };

  return (
    <div className="flex min-h-dvh flex-col bg-surface text-fg">
      <div className="flex justify-end p-3">
        <ThemeMenu />
      </div>

      <main className="flex flex-1 items-start justify-center px-4 pb-16 pt-4 sm:items-center sm:pt-0">
        <div className="w-full max-w-sm rounded-xl border border-border bg-surface p-6 shadow-sm">
          <div className="mb-5 flex items-center justify-between gap-2">
            <h1 className="text-xl font-semibold">{t('auth.title')}</h1>
            <LanguageMenu />
          </div>

          <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label htmlFor="login-email" className="text-sm font-medium">
                {t('auth.identifierLabel')}
              </label>
              <input
                id="login-email"
                name="email"
                type="text"
                autoComplete="username"
                autoFocus
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={error !== null}
                aria-describedby={error ? errorId : undefined}
                placeholder={t('auth.emailPlaceholder')}
                className="rounded-md border border-border bg-surface px-3 py-2 text-fg placeholder:text-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              />
            </div>

            <div className="flex flex-col gap-1">
              <label htmlFor="login-password" className="text-sm font-medium">
                {t('auth.passwordLabel')}
              </label>
              <PasswordInput
                id="login-password"
                name="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                aria-invalid={error !== null}
                aria-describedby={error ? errorId : undefined}
              />
            </div>

            <label htmlFor="login-stay" className="flex items-center gap-2 text-sm">
              <input
                id="login-stay"
                type="checkbox"
                checked={stayLoggedIn}
                onChange={(e) => setStayLoggedIn(e.target.checked)}
                className="h-4 w-4 rounded border-border-strong text-brand focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              />
              {t('auth.stayLoggedIn')}
            </label>

            {error && (
              <p id={errorId} role="alert" className="text-sm text-danger">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="mt-2 rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg transition-colors hover:bg-brand/90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand disabled:opacity-60 motion-reduce:transition-none"
            >
              {submitting ? t('auth.submitting') : t('auth.submit')}
            </button>

            {accounts.length > 0 && (
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="text-center text-sm text-muted underline-offset-2 hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
              >
                {t('auth.cancelAddAccount')}
              </button>
            )}
          </form>
        </div>
      </main>
    </div>
  );
}
